<template>
  <a-authorization-card
    v-if="associationRadieeOffice"
    class="ma-5"
    color="error"
    icon="mdi-cancel"
    title="Accès refusé"
    text="L'association est radiée d'office du registre."
  />
  <div v-else>
    <a-association-toolbar
      :numero="numero"
      :value="currentVersionAssociation"
      :juridiction="juridiction"
      :premiereVersionRadieeOffice="premiereVersionRadieeOffice"
    />
    <v-container>
      <a-fiche-suivi-extrait-dialog
        ref="dialog"
        :association="numero"
        :avecRegistre="avecRegistre"
        :juridiction="juridictionPourExtrait"
      />
      <v-row class="mb-1" no-gutters>
        <v-col cols="12" md="8">
          <v-tabs color="secondary" v-model="tab" show-arrows grow>
            <v-tab data-testid="association-onglet-informations"> Informations </v-tab>
            <v-tab v-if="avecMembres" data-testid="association-onglet-membres">
              Membres
            </v-tab>
            <v-tab data-testid="association-onglet-dcuments"> Documents </v-tab>
            <v-tab v-if="avecRegistre" data-testid="association-onglet-registre-papier">
              Registre papier
            </v-tab>
            <v-tab v-if="avecUsagers" data-testid="association-onglet-utilisateurs">
              Utilisateurs</v-tab
            >
          </v-tabs>
        </v-col>
        <v-col
          cols="12"
          md="4"
          class="d-flex align-center justify-end background mt-sm-2 mt-xs-2"
        >
          <v-btn
            color="info"
            v-if="rattachementPossible"
            @click="demandeRattachement"
            class="mr-1"
            data-testid="demander-rattachement"
            >Demander rattachement</v-btn
          >

          <template v-if="libelleRattachement">
            <div class="mr-2">
              <v-chip color="info" data-testid="association-etat-rattachement">{{
                libelleRattachement
              }}</v-chip>
            </div>
          </template>

          <a-fiche-suivi-numerisation-dialog ref="numerisationDialog" />
          <a-fiche-suivi-rattachement-dialog ref="rattachementDialog" />

          <v-menu
            v-model="menu"
            offset-y
            transition="scroll-y-transition"
            nudge-bottom="5"
          >
            <template v-slot:activator="{ on, props }">
              <v-btn data-testid="actions" v-bind="props" v-on="on" color="primary">
                Actions
              </v-btn>
            </template>

            <v-list>
              <v-list-item
                v-for="(action, i) in actions"
                :key="i"
                :disabled="action.disabled"
                @click="action.callback"
                :data-testid="action.dataTestId"
              >
                <v-list-item-icon>
                  <v-icon>{{ action.icon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ action.libelle }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider />
              <v-list-item
                data-testid="ouvre-aide"
                @click="ouvreAide"
                class="primary white--text"
              >
                <v-list-item-icon>
                  <v-icon class="white--text">mdi-information-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>
                    Pourquoi certaines actions sont indisponibles ?
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
          <a-fiche-association-aide-dialog ref="aide" />
          <v-btn
            data-testid="historique"
            @click.stop="drawer = !drawer"
            class="ml-2"
            v-if="avecUsagers"
          >
            <v-icon>mdi-history</v-icon>
            <span class="d-none d-lg-flex">Historique</span>
          </v-btn>
        </v-col>
      </v-row>
      <v-tabs-items v-model="tab" class="background">
        <v-tab-item v-if="currentVersionAssociation">
          <a-fiche-suivi
            :value="currentVersionAssociation"
            :key="currentVersionAssociation.version"
          />
        </v-tab-item>
        <v-tab-item v-if="currentVersionAssociation && avecMembres">
          <a-fiche-suivi-membres :value="currentVersionAssociation" />
        </v-tab-item>
        <v-tab-item>
          <a-fiche-suivi-documents
            :numero="numero"
            :versionId="currentVersionId"
            :documents="currentVersionDocuments"
            :numerisation="numerisation"
          />
        </v-tab-item>
        <v-tab-item v-if="reprise">
          <a-fiche-suivi-reprise :reprise="reprise" />
        </v-tab-item>
        <v-tab-item v-if="avecUsagers">
          <a-invitation-usager-dialog ref="invitation" />
          <a-supprime-invitation-dialog ref="supprimeInvitation" />
          <a-modification-usager-dialog ref="modification" />
          <a-supprime-usager-dialog ref="supprime" />
          <a-reenvoi-invitation-dialog ref="reenvoiInvitation" />
          <a-fiche-suivi-administrateurs
            :usagers="usagers"
            :rattachementsDemandes="rattachementsDemandes"
            :invitations="invitations"
            :gestion="rattachement?.gestion"
            @supprime="ouvreSupprimeUsager"
            @modifie="ouvreModifieUsager"
            @invite="inviteUsager"
            @supprimeInvitation="ouvreSupprimeInvitation"
            @reenvoiInvitation="ouvreReenvoiInvitation"
          />
        </v-tab-item>
      </v-tabs-items>
      <v-navigation-drawer v-model="drawer" fixed right width="45%" bottom temporary>
        <a-association-historique
          :evenementsAssociation="historiqueAssociations"
          :itemKey="itemKey"
          @select="selectVersion"
          :depot="depot"
        />
      </v-navigation-drawer>
    </v-container>
  </div>
</template>

<script>
import makeRouteLoader from "@/mixins/makeRouteLoader";
import { associationService } from "@/services";
import date from "@/filters/date";
import copy from "@/services/copy";
import {
  toModificationAssociation,
  toDissolutionAssociation,
  toRadiationAssociation,
} from "@/routes/associations";

function invitationPredicate(invitation) {
  return u => u.tacheRattachement === invitation.tacheRattachement;
}

export default {
  props: {
    numero: {
      type: String,
      required: true,
    },
  },
  mixins: [
    makeRouteLoader({
      promise({ numero }) {
        return associationService.chargeFicheAssociation(numero);
      },
      next({ vm, body }) {
        vm.charge(body);
      },
    }),
  ],
  data() {
    return {
      tab: 0,
      menu: false,
      drawer: null,
      reprise: null,
      numerisation: null,
      versionsAssociations: [],
      lastVersion: {},
      currentVersion: {},
      itemKey: null,
      dialogCreationRequete: false,
      rattachement: null,
      nonConformites: false,
      generationExtrait: false,
      usagers: [],
      rattachementsDemandes: null,
      invitations: null,
      juridiction: null,
      associationRadieeOffice: false,
    };
  },
  computed: {
    libelleRattachement() {
      const etat = this.rattachement?.etat;
      if ("EN_COURS" === etat) {
        return "Rattachement en cours";
      } else if ("INVITATION_EN_COURS" === etat) {
        return "Invitation en cours";
      } else if ("RATTACHE" === etat) {
        return "Rattaché";
      }
      return null;
    },
    juridictionPourExtrait() {
      return !this.avecRegistre && !!this.numerisation ? this.juridiction : null;
    },
    actions() {
      const actions = [];
      actions.push({
        icon: "mdi-receipt",
        libelle: "Générer un extrait",
        disabled: this.nonConformites || !this.generationExtrait,
        dataTestId: "generer-extrait",
        callback: () => this.openExtraitDialog(),
      });
      let tooltipRequete = null;
      actions.push({
        icon: "mdi-file-plus",
        libelle: "Déposer une requête de modification",
        dataTestId: "deposer-requete-modification",
        disabled: this.avecRequeteEnCours || !this.modificationPossible,
        callback: () => this.demarreModification(),
        tooltip: tooltipRequete,
      });

      actions.push({
        icon: "mdi-file-clock",
        libelle: "Déposer une requête de dissolution",
        dataTestId: "deposer-requete-dissolution",
        disabled: this.avecRequeteEnCours || !this.modificationPossible,
        callback: () => this.demarreDissolution(),
        tooltip: tooltipRequete,
      });

      actions.push({
        icon: "mdi-file-remove",
        libelle: "Déposer une requête de radiation",
        dataTestId: "deposer-requete-radiation",
        disabled: this.avecRequeteEnCours || !this.radiationPossible,
        callback: () => this.demarreRadiation(),
        tooltip: tooltipRequete,
      });

      actions.push({
        icon: "mdi-printer",
        libelle: "Demander une numérisation des archives",
        dataTestId: "demander-numerisation-archives",
        disabled: !this.demandeNumerisationPossible,
        callback: () => this.demandeNumerisation(),
      });

      return actions;
    },
    premiereVersionRadieeOffice() {
      if (this.historiqueAssociations && this.historiqueAssociations.length) {
        const premier = this.historiqueAssociations[
          this.historiqueAssociations.length - 1
        ];
        return "RADIEE_OFFICE" === premier.etatAssociation;
      }
      return false;
    },
    modificationPossible() {
      return this.depot && "INSCRITE" === this.dernierEtatAssociation;
    },
    radiationPossible() {
      return this.depot && "DISSOUTE" === this.dernierEtatAssociation;
    },
    demandeNumerisationPossible() {
      return ["POSSIBLE", "EN_COURS"].includes(this.numerisation?.etat);
    },
    rattachementPossible() {
      return ["NON_RATTACHE"].includes(this.rattachement?.etat);
    },
    avecRegistre() {
      return this.reprise != null && this.avecUsagers;
    },
    avecUsagers() {
      return "RATTACHE" === this.rattachement?.etat;
    },
    depot() {
      return !!this.rattachement?.depot;
    },
    avecMembres() {
      const association = this.currentVersionAssociation;
      if (association) {
        return !!association.reprises || !!association.morales || !!association.physiques;
      }
      return false;
    },
    avecRequeteEnCours() {
      return this.versionsAssociations.some(
        v => v.requete && !v.requete?.resultat && !v.association
      );
    },
    historiqueAssociations() {
      if (this.versionsAssociations?.length) {
        return this.versionsAssociations.map(v => {
          return {
            ...v.evenement,
            etatAssociation: v.association?.etatAssociation,
            association: v.association,
            requete: v.requete,
            numero: v.requete?.numero,
            typeRequete: v.requete?.type,
            sousTypes: v.requete?.sousTypes || [],
            extrait: v.extrait,
          };
        });
      }
      return null;
    },
    currentVersionId() {
      return this.currentVersion?.association?.version;
    },
    currentVersionAssociation() {
      if (this.currentVersion?.association) {
        const association = this.currentVersion.association;
        return {
          ...association,
          estLiquidee: !!association.liquidationAssociation,
          estRadiee: !!association.radiationAssociation,
          evenementDate: this.currentVersionEvenementDateCreation,
        };
      }
      return {};
    },
    dernierEtatAssociation() {
      return this.lastVersion?.association?.etatAssociation;
    },
    dernierDenominationAssociation() {
      return this.lastVersion?.association?.informations?.denomination;
    },
    currentVersionEvenementDateCreation() {
      return this.currentVersion?.evenement?.dateCreation;
    },
    etatAssociation() {
      return this.currentVersion?.association?.etatAssociation;
    },

    currentVersionDocuments() {
      return (this.currentVersion?.association?.documents || []).filter(d => !!d.taille);
    },
    dateOrdonnanceDissolution() {
      const evts = this.historiqueAssociations.filter(e => {
        const requete = e.requete;
        if (requete) {
          return "DISSOLUTION" === requete.type && "INSCRITE" === requete.resultat;
        }
        return true;
      });
      if (evts.length > 0) {
        var datePart = date(evts[0].dateValeur).match(/\d+/g);
        return datePart[2] + "-" + datePart[1] + "-" + datePart[0];
      }
      return null;
    },
  },
  methods: {
    ouvreAide() {
      this.$refs.aide.ouvre();
    },
    charge(body) {
      if (!body.associationRehydrateVisibles) {
        this.associationRadieeOffice = true;
      } else {
        this.associationRadieeOffice = false;

        this.reprise = body.reprise;
        this.numerisation = body.numerisation;
        this.rattachement = body.rattachement;
        this.usagers = body.usagers;
        this.rattachementsDemandes = body.rattachementsDemandes;
        this.invitations = body.invitations;
        this.numerisation = body.numerisation;
        this.nonConformites = body.nonConformites;
        this.generationExtrait = body.generationExtrait;
        this.versionsAssociations = body.associationRehydrateVisibles.reverse();
        this.juridiction = body.juridiction;
        if (this.versionsAssociations.length) {
          const version = this.versionsAssociations.findIndex(v => !!v.association);
          const lastVersion = this.selectVersion(version);
          this.lastVersion = lastVersion;
        }
      }
    },
    prepareRequete() {
      // stockage des informations de l'association dans le store
      this.$store.commit("modificationAssociation/init", {
        numero: this.numero,
        association: this.lastVersion.association,
        fonction: this.rattachement?.fonction,
        juridiction: this.juridiction,
        dateDissolution: this.dateOrdonnanceDissolution,
      });
    },
    selectVersion(key) {
      this.drawer = false;
      this.itemKey = key;
      this.currentVersion = copy(this.versionsAssociations[this.itemKey]);
      return this.currentVersion;
    },
    ouvreSupprimeUsager(usager) {
      const { uuid } = usager;
      this.$refs.supprime.demarre(this.numero, uuid, rattachement => {
        this.majRattachement(rattachement);
        this.usagers = this.usagers.filter(u => u.uuid !== uuid);
      });
    },
    ouvreSupprimeInvitation(invitation) {
      this.$refs.supprimeInvitation.demarre(this.numero, invitation, () => {
        var predicate = invitationPredicate(invitation);
        this.invitations = this.invitations.filter(u => !predicate(u));
      });
    },
    ouvreReenvoiInvitation(invitation) {
      this.$refs.reenvoiInvitation.demarre(this.numero, invitation);
    },
    majRattachement({ etat, fonction, gestion, depot }) {
      //  les valeurs viennent du serveur pour soi meme
      this.rattachement = {
        etat,
        gestion,
        depot,
        fonction,
      };
      this.$store.commit("modificationAssociation/fonction", {
        fonction,
      });
    },
    ouvreModifieUsager(usager) {
      const { uuid } = usager;
      this.$refs.modification.ouvre(
        this.numero,
        usager,
        (nouvelleValeurs, rattachement) => {
          this.majRattachement(rattachement);
          // mise à jour des usagers
          this.usagers = this.usagers.map(u => {
            if (u.uuid === uuid) {
              return {
                ...u,
                ...nouvelleValeurs,
              };
            }
            return u;
          });
        }
      );
    },
    inviteUsager() {
      this.$refs.invitation.ouvre(this.numero, invitation => {
        const index = this.invitations.findIndex(invitationPredicate(invitation));
        if (index >= 0) this.invitations.splice(index, 1, invitation);
        else this.invitations.push(invitation);
      });
    },
    demarreModification() {
      this.prepareRequete();
      this.$router.push(toModificationAssociation(this.numero));
    },
    demarreDissolution() {
      this.prepareRequete();
      this.$router.push(toDissolutionAssociation(this.numero));
    },
    demarreRadiation() {
      this.prepareRequete();
      this.$router.push(toRadiationAssociation(this.numero));
    },
    demandeNumerisation() {
      this.$refs.numerisationDialog.ouvre(this.numero, etat => {
        this.numerisation = {
          ...this.numerisation,
          etat,
        };
      });
    },
    demandeRattachement() {
      this.$refs.rattachementDialog.ouvre(
        this.numero,
        this.dernierDenominationAssociation,
        reponse => {
          this.rattachement = {
            ...this.rattachement,
            etat: reponse.etat,
          };
        }
      );
    },
    openExtraitDialog() {
      this.$refs.dialog.ouvre();
    },
  },
};
</script>
